import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

// Initialize Supabase client
const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

interface AnalyticsData {
  visitors: {
    unique: number;
    total_pageviews: number;
  };
  opt_ins: {
    count: number;
    rate: number;
  };
  affiliate_clicks: {
    count: number;
    rate: number;
  };
  overall_conversion_rate: number;
  utm_breakdown: Record<string, {
    visits: number;
    opt_ins: number;
    affiliate_clicks: number;
    opt_in_rate: number;
    click_through_rate: number;
    overall_conversion_rate: number;
  }>;
  daily_trend: Array<{
    date: string;
    visitors: number;
    opt_ins: number;
    affiliate_clicks: number;
  }>;
  device_breakdown: {
    desktop: number;
    mobile: number;
    tablet: number;
  };
}

interface APIKey {
  id: string;
  name: string;
  key: string;
  created_at: string;
  revoked_at: string | null;
  last_used_at: string | null;
}

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [sortField, setSortField] = useState<'visits' | 'opt_ins' | 'affiliate_clicks'>('visits');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [activeDatePreset, setActiveDatePreset] = useState<'today' | '7days' | '30days' | 'custom'>('7days');
  const [showCustomRange, setShowCustomRange] = useState(false);

  // API Keys state
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [loadingKeys, setLoadingKeys] = useState(false);

  const ADMIN_PASSWORD = 'EpicStats26!';
  const SESSION_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days

  const getPresetDates = (preset: 'today' | '7days' | '30days') => {
    const today = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const fmt = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    if (preset === 'today') {
      const s = fmt(today);
      return { start: s, end: s };
    }
    if (preset === '7days') {
      const s = new Date(today);
      s.setDate(today.getDate() - 6);
      return { start: fmt(s), end: fmt(today) };
    }
    // 30days
    const s = new Date(today);
    s.setDate(today.getDate() - 29);
    return { start: fmt(s), end: fmt(today) };
  };

  const handlePresetClick = (preset: 'today' | '7days' | '30days') => {
    setActiveDatePreset(preset);
    setShowCustomRange(false);
    const { start, end } = getPresetDates(preset);
    setDateRange({ start, end });
    loadAnalytics(start, end);
  };

  const handleCustomClick = () => {
    setActiveDatePreset('custom');
    setShowCustomRange(true);
  };

  useEffect(() => {
    // Check if already authenticated
    const authData = localStorage.getItem('admin_auth');
    if (authData) {
      const { timestamp } = JSON.parse(authData);
      const now = Date.now();
      
      if (now - timestamp < SESSION_DURATION) {
        setIsAuthenticated(true);
        const { start, end } = getPresetDates('7days');
        loadAnalytics(start, end);
        loadAPIKeys();
      } else {
        localStorage.removeItem('admin_auth');
      }
    }
  }, []);

  const loadAnalytics = async (start?: string, end?: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('analytics', {
        method: 'GET',
        headers: {
          'X-API-Key': 'hedgethefed_analytics_2024',
        },
      });

      if (error) throw error;
      setAnalyticsData(data);
    } catch (err) {
      console.error('Failed to load analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAPIKeys = async () => {
    setLoadingKeys(true);
    try {
      const { data, error } = await supabase
        .from('api_keys')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setApiKeys(data || []);
    } catch (err) {
      console.error('Failed to load API keys:', err);
    } finally {
      setLoadingKeys(false);
    }
  };

  const generateAPIKey = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let key = 'htf_';
    for (let i = 0; i < 32; i++) {
      key += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return key;
  };

  const handleCreateKey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newKeyName.trim()) return;

    const newKey = generateAPIKey();
    
    try {
      const { error } = await supabase
        .from('api_keys')
        .insert({
          name: newKeyName.trim(),
          key: newKey,
        });

      if (error) throw error;

      setCreatedKey(newKey);
      setNewKeyName('');
      setShowCreateForm(false);
      loadAPIKeys();
    } catch (err) {
      console.error('Failed to create API key:', err);
      alert('Failed to create API key. Please try again.');
    }
  };

  const handleRevokeKey = async (keyId: string, keyName: string) => {
    if (!confirm(`Are you sure you want to revoke the API key "${keyName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('api_keys')
        .update({ revoked_at: new Date().toISOString() })
        .eq('id', keyId);

      if (error) throw error;
      loadAPIKeys();
    } catch (err) {
      console.error('Failed to revoke API key:', err);
      alert('Failed to revoke API key. Please try again.');
    }
  };

  const maskKey = (key: string) => {
    if (key.length <= 8) return key;
    return key.substring(0, 4) + '••••' + key.substring(key.length - 4);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      localStorage.setItem('admin_auth', JSON.stringify({ timestamp: Date.now() }));
      setError('');
      const { start, end } = getPresetDates('7days');
      loadAnalytics(start, end);
      loadAPIKeys();
    } else {
      setError('Incorrect password');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('admin_auth');
    setPassword('');
  };

  const handleDateFilter = () => {
    if (dateRange.start || dateRange.end) {
      loadAnalytics(dateRange.start, dateRange.end);
    }
  };

  const handleClearFilter = () => {
    setDateRange({ start: '', end: '' });
    loadAnalytics();
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  const formatPercent = (num: number) => {
    return `${num.toFixed(1)}%`;
  };

  const getSortedUTMData = () => {
    if (!analyticsData) return [];
    
    const entries = Object.entries(analyticsData.utm_breakdown).map(([campaign, data]) => ({
      campaign,
      ...data,
    }));

    return entries.sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      return sortDirection === 'desc' ? bVal - aVal : aVal - bVal;
    });
  };

  const handleSort = (field: 'visits' | 'opt_ins' | 'affiliate_clicks') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center px-4">
        <div className="bg-slate-800 rounded-xl shadow-2xl p-8 w-full max-w-md border border-slate-700">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-shield-keyhole-line text-3xl text-white"></i>
            </div>
            <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
            <p className="text-slate-400 mt-2">Enter password to access analytics</p>
          </div>

          <form onSubmit={handleLogin}>
            <div className="mb-6">
              <label htmlFor="password" className="block text-sm font-medium text-slate-300 mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all text-white"
                placeholder="Enter admin password"
              />
              {error && (
                <p className="text-red-400 text-sm mt-2 flex items-center">
                  <i className="ri-error-warning-line mr-1"></i>
                  {error}
                </p>
              )}
            </div>

            <button
              type="submit"
              className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold py-3 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
            >
              Access Dashboard
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (loading && !analyticsData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Loading analytics...</p>
        </div>
      </div>
    );
  }

  const optInRate = analyticsData?.opt_ins.rate || 0;
  const clickThroughRate = analyticsData?.affiliate_clicks.rate || 0;
  const overallConversionRate = analyticsData?.overall_conversion_rate || 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">Analytics Dashboard</h1>
            <p className="text-slate-400">HedgeTheFed.com — Two-Step Funnel Tracking</p>
          </div>
          <button
            onClick={handleLogout}
            className="bg-slate-800 hover:bg-slate-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer flex items-center border border-slate-700"
          >
            <i className="ri-logout-box-line mr-2"></i>
            Logout
          </button>
        </div>

        {/* Date Filter */}
        <div className="bg-slate-800 rounded-xl p-6 mb-8 border border-slate-700">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-slate-300 font-medium text-sm whitespace-nowrap">Date Range:</span>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handlePresetClick('today')}
                className={`px-5 py-2 rounded-lg font-semibold text-sm transition-colors whitespace-nowrap cursor-pointer ${
                  activeDatePreset === 'today'
                    ? 'bg-teal-500 text-white'
                    : 'bg-slate-700 hover:bg-slate-600 text-slate-200'
                }`}
              >
                Today
              </button>
              <button
                onClick={() => handlePresetClick('7days')}
                className={`px-5 py-2 rounded-lg font-semibold text-sm transition-colors whitespace-nowrap cursor-pointer ${
                  activeDatePreset === '7days'
                    ? 'bg-teal-500 text-white'
                    : 'bg-slate-700 hover:bg-slate-600 text-slate-200'
                }`}
              >
                Last 7 Days
              </button>
              <button
                onClick={() => handlePresetClick('30days')}
                className={`px-5 py-2 rounded-lg font-semibold text-sm transition-colors whitespace-nowrap cursor-pointer ${
                  activeDatePreset === '30days'
                    ? 'bg-teal-500 text-white'
                    : 'bg-slate-700 hover:bg-slate-600 text-slate-200'
                }`}
              >
                Last 30 Days
              </button>
              <button
                onClick={handleCustomClick}
                className={`px-5 py-2 rounded-lg font-semibold text-sm transition-colors whitespace-nowrap cursor-pointer ${
                  activeDatePreset === 'custom'
                    ? 'bg-teal-500 text-white'
                    : 'bg-slate-700 hover:bg-slate-600 text-slate-200'
                }`}
              >
                Custom Range
              </button>
            </div>
          </div>

          {/* Custom Range Inputs */}
          {showCustomRange && (
            <div className="flex flex-wrap items-end gap-4 mt-4 pt-4 border-t border-slate-700">
              <div className="flex-1 min-w-[180px]">
                <label className="block text-sm font-medium text-slate-300 mb-2">Start Date</label>
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-teal-500 outline-none text-sm"
                />
              </div>
              <div className="flex-1 min-w-[180px]">
                <label className="block text-sm font-medium text-slate-300 mb-2">End Date</label>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-teal-500 outline-none text-sm"
                />
              </div>
              <button
                onClick={handleDateFilter}
                className="bg-teal-500 hover:bg-teal-600 text-white px-6 py-2 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer text-sm"
              >
                Apply
              </button>
            </div>
          )}
        </div>

        {/* Top Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Visitors */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <i className="ri-user-line text-2xl text-blue-400"></i>
              </div>
            </div>
            <div className="text-3xl font-bold text-white mb-1">
              {formatNumber(analyticsData?.visitors.unique || 0)}
            </div>
            <p className="text-slate-400 text-sm">Unique Visitors</p>
            <p className="text-slate-500 text-xs mt-1">
              {formatNumber(analyticsData?.visitors.total_pageviews || 0)} total pageviews
            </p>
          </div>

          {/* Opt-ins (Step 1) */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                <i className="ri-mail-check-line text-2xl text-green-400"></i>
              </div>
            </div>
            <div className="text-3xl font-bold text-white mb-1">
              {formatNumber(analyticsData?.opt_ins.count || 0)}
            </div>
            <p className="text-slate-400 text-sm">Opt-ins (Step 1)</p>
            <p className="text-green-400 text-xs mt-1 font-semibold">
              {formatPercent(optInRate)} conversion rate
            </p>
          </div>

          {/* Affiliate Clicks (Step 2) */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center">
                <i className="ri-external-link-line text-2xl text-amber-400"></i>
              </div>
            </div>
            <div className="text-3xl font-bold text-white mb-1">
              {formatNumber(analyticsData?.affiliate_clicks.count || 0)}
            </div>
            <p className="text-slate-400 text-sm">Affiliate Clicks (Step 2)</p>
            <p className="text-amber-400 text-xs mt-1 font-semibold">
              {formatPercent(clickThroughRate)} click-through rate
            </p>
          </div>

          {/* Overall Conversion */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <i className="ri-trophy-line text-2xl text-purple-400"></i>
              </div>
            </div>
            <div className="text-3xl font-bold text-white mb-1">
              {formatPercent(overallConversionRate)}
            </div>
            <p className="text-slate-400 text-sm">Overall Conversion</p>
            <p className="text-slate-500 text-xs mt-1">
              Visitors → Affiliate Clicks
            </p>
          </div>
        </div>

        {/* Funnel Visualization */}
        <div className="bg-slate-800 rounded-xl p-8 mb-8 border border-slate-700">
          <h2 className="text-2xl font-bold text-white mb-6">Two-Step Funnel</h2>
          <div className="flex items-center justify-between max-w-4xl mx-auto">
            {/* Step 1: Visitors */}
            <div className="text-center flex-1">
              <div className="w-32 h-32 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-blue-500">
                <div>
                  <div className="text-3xl font-bold text-white">
                    {formatNumber(analyticsData?.visitors.unique || 0)}
                  </div>
                  <div className="text-xs text-blue-400 mt-1">Visitors</div>
                </div>
              </div>
              <p className="text-slate-400 text-sm font-semibold">Front Page</p>
            </div>

            {/* Arrow 1 */}
            <div className="flex flex-col items-center px-4">
              <i className="ri-arrow-right-line text-4xl text-slate-600 mb-2"></i>
              <div className="text-green-400 font-bold text-lg">{formatPercent(optInRate)}</div>
              <div className="text-slate-500 text-xs">opt-in rate</div>
            </div>

            {/* Step 2: Opt-ins */}
            <div className="text-center flex-1">
              <div className="w-32 h-32 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-green-500">
                <div>
                  <div className="text-3xl font-bold text-white">
                    {formatNumber(analyticsData?.opt_ins.count || 0)}
                  </div>
                  <div className="text-xs text-green-400 mt-1">Opt-ins</div>
                </div>
              </div>
              <p className="text-slate-400 text-sm font-semibold">Step 1</p>
            </div>

            {/* Arrow 2 */}
            <div className="flex flex-col items-center px-4">
              <i className="ri-arrow-right-line text-4xl text-slate-600 mb-2"></i>
              <div className="text-amber-400 font-bold text-lg">{formatPercent(clickThroughRate)}</div>
              <div className="text-slate-500 text-xs">click-through</div>
            </div>

            {/* Step 3: Affiliate Clicks */}
            <div className="text-center flex-1">
              <div className="w-32 h-32 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-amber-500">
                <div>
                  <div className="text-3xl font-bold text-white">
                    {formatNumber(analyticsData?.affiliate_clicks.count || 0)}
                  </div>
                  <div className="text-xs text-amber-400 mt-1">Clicks</div>
                </div>
              </div>
              <p className="text-slate-400 text-sm font-semibold">Step 2</p>
            </div>
          </div>
        </div>

        {/* Daily Trend Chart */}
        <div className="bg-slate-800 rounded-xl p-8 mb-8 border border-slate-700">
          <h2 className="text-2xl font-bold text-white mb-6">Daily Trend</h2>
          {analyticsData?.daily_trend && analyticsData.daily_trend.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-slate-400 font-semibold text-sm">Date</th>
                    <th className="text-right py-3 px-4 text-slate-400 font-semibold text-sm">Visitors</th>
                    <th className="text-right py-3 px-4 text-slate-400 font-semibold text-sm">Opt-ins</th>
                    <th className="text-right py-3 px-4 text-slate-400 font-semibold text-sm">Affiliate Clicks</th>
                  </tr>
                </thead>
                <tbody>
                  {analyticsData.daily_trend.map((day) => (
                    <tr key={day.date} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                      <td className="py-3 px-4 text-white">{day.date}</td>
                      <td className="py-3 px-4 text-right text-blue-400">{formatNumber(day.visitors)}</td>
                      <td className="py-3 px-4 text-right text-green-400">{formatNumber(day.opt_ins)}</td>
                      <td className="py-3 px-4 text-right text-amber-400">{formatNumber(day.affiliate_clicks)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">No trend data available</p>
          )}
        </div>

        {/* UTM Campaign Breakdown */}
        <div className="bg-slate-800 rounded-xl p-8 mb-8 border border-slate-700">
          <h2 className="text-2xl font-bold text-white mb-6">UTM Campaign Breakdown</h2>
          {getSortedUTMData().length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-slate-400 font-semibold text-sm">Campaign</th>
                    <th 
                      className="text-right py-3 px-4 text-slate-400 font-semibold text-sm cursor-pointer hover:text-white"
                      onClick={() => handleSort('visits')}
                    >
                      Visits {sortField === 'visits' && (sortDirection === 'desc' ? '↓' : '↑')}
                    </th>
                    <th 
                      className="text-right py-3 px-4 text-slate-400 font-semibold text-sm cursor-pointer hover:text-white"
                      onClick={() => handleSort('opt_ins')}
                    >
                      Opt-ins {sortField === 'opt_ins' && (sortDirection === 'desc' ? '↓' : '↑')}
                    </th>
                    <th className="text-right py-3 px-4 text-slate-400 font-semibold text-sm">Opt-in Rate</th>
                    <th 
                      className="text-right py-3 px-4 text-slate-400 font-semibold text-sm cursor-pointer hover:text-white"
                      onClick={() => handleSort('affiliate_clicks')}
                    >
                      Clicks {sortField === 'affiliate_clicks' && (sortDirection === 'desc' ? '↓' : '↑')}
                    </th>
                    <th className="text-right py-3 px-4 text-slate-400 font-semibold text-sm">CTR</th>
                    <th className="text-right py-3 px-4 text-slate-400 font-semibold text-sm">Overall Conv.</th>
                  </tr>
                </thead>
                <tbody>
                  {getSortedUTMData().map((row) => (
                    <tr key={row.campaign} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                      <td className="py-3 px-4 text-white font-medium">{row.campaign}</td>
                      <td className="py-3 px-4 text-right text-blue-400">{formatNumber(row.visits)}</td>
                      <td className="py-3 px-4 text-right text-green-400">{formatNumber(row.opt_ins)}</td>
                      <td className="py-3 px-4 text-right text-slate-300">{formatPercent(row.opt_in_rate)}</td>
                      <td className="py-3 px-4 text-right text-amber-400">{formatNumber(row.affiliate_clicks)}</td>
                      <td className="py-3 px-4 text-right text-slate-300">{formatPercent(row.click_through_rate)}</td>
                      <td className="py-3 px-4 text-right text-purple-400">{formatPercent(row.overall_conversion_rate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">No UTM data available</p>
          )}
        </div>

        {/* Device Breakdown */}
        <div className="bg-slate-800 rounded-xl p-8 border border-slate-700">
          <h2 className="text-2xl font-bold text-white mb-6">Device Breakdown</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-computer-line text-3xl text-blue-400"></i>
              </div>
              <div className="text-3xl font-bold text-white mb-1">
                {formatNumber(analyticsData?.device_breakdown.desktop || 0)}
              </div>
              <p className="text-slate-400 text-sm">Desktop</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-smartphone-line text-3xl text-green-400"></i>
              </div>
              <div className="text-3xl font-bold text-white mb-1">
                {formatNumber(analyticsData?.device_breakdown.mobile || 0)}
              </div>
              <p className="text-slate-400 text-sm">Mobile</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-tablet-line text-3xl text-purple-400"></i>
              </div>
              <div className="text-3xl font-bold text-white mb-1">
                {formatNumber(analyticsData?.device_breakdown.tablet || 0)}
              </div>
              <p className="text-slate-400 text-sm">Tablet</p>
            </div>
          </div>
        </div>

        {/* API Keys Management */}
        <div className="bg-slate-800 rounded-xl p-8 border border-slate-700">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-white">API Keys</h2>
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-2 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer flex items-center"
            >
              <i className="ri-add-line mr-2"></i>
              Create New Key
            </button>
          </div>

          {/* Create Key Form */}
          {showCreateForm && (
            <div className="bg-slate-900 rounded-lg p-6 mb-6 border border-slate-700">
              <form onSubmit={handleCreateKey}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Key Name
                  </label>
                  <input
                    type="text"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    placeholder="e.g., Production API, Mobile App, Analytics Dashboard"
                    className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-amber-500 outline-none"
                    required
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    type="submit"
                    className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Generate Key
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateForm(false);
                      setNewKeyName('');
                    }}
                    className="bg-slate-700 hover:bg-slate-600 text-white px-6 py-2 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Created Key Display */}
          {createdKey && (
            <div className="bg-green-500/10 border border-green-500 rounded-lg p-6 mb-6">
              <div className="flex items-start gap-3 mb-4">
                <i className="ri-information-line text-2xl text-green-400 mt-1"></i>
                <div>
                  <h3 className="text-green-400 font-bold text-lg mb-1">API Key Created Successfully!</h3>
                  <p className="text-slate-300 text-sm">
                    Save this key now — it won't be shown again. Store it securely.
                  </p>
                </div>
              </div>
              <div className="bg-slate-900 rounded-lg p-4 flex items-center justify-between">
                <code className="text-green-400 font-mono text-sm break-all">{createdKey}</code>
                <button
                  onClick={() => copyToClipboard(createdKey)}
                  className="ml-4 bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer flex items-center"
                >
                  <i className="ri-file-copy-line mr-2"></i>
                  Copy
                </button>
              </div>
              <button
                onClick={() => setCreatedKey(null)}
                className="mt-4 text-slate-400 hover:text-white text-sm cursor-pointer"
              >
                I've saved this key, dismiss this message
              </button>
            </div>
          )}

          {/* API Keys Table */}
          {loadingKeys ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-slate-400">Loading API keys...</p>
            </div>
          ) : apiKeys.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-slate-400 font-semibold text-sm">Name</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-semibold text-sm">Key</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-semibold text-sm">Created</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-semibold text-sm">Last Used</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-semibold text-sm">Status</th>
                    <th className="text-right py-3 px-4 text-slate-400 font-semibold text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {apiKeys.map((key) => (
                    <tr 
                      key={key.id} 
                      className={`border-b border-slate-700/50 hover:bg-slate-700/30 ${key.revoked_at ? 'opacity-50' : ''}`}
                    >
                      <td className="py-3 px-4 text-white font-medium">{key.name}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <code className="text-slate-300 font-mono text-sm">{maskKey(key.key)}</code>
                          <button
                            onClick={() => copyToClipboard(key.key)}
                            className="text-slate-400 hover:text-white cursor-pointer"
                            title="Copy full key"
                          >
                            <i className="ri-file-copy-line"></i>
                          </button>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-slate-400 text-sm">
                        {formatDate(key.created_at)}
                      </td>
                      <td className="py-3 px-4 text-slate-400 text-sm">
                        {formatDate(key.last_used_at)}
                      </td>
                      <td className="py-3 px-4">
                        {key.revoked_at ? (
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-red-500/20 text-red-400">
                            <i className="ri-close-circle-line mr-1"></i>
                            Revoked
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-500/20 text-green-400">
                            <i className="ri-checkbox-circle-line mr-1"></i>
                            Active
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        {!key.revoked_at && (
                          <button
                            onClick={() => handleRevokeKey(key.id, key.name)}
                            className="bg-red-500/20 hover:bg-red-500/30 text-red-400 px-4 py-1 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer text-sm"
                          >
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-key-line text-3xl text-slate-500"></i>
              </div>
              <p className="text-slate-400 mb-2">No API keys created yet</p>
              <p className="text-slate-500 text-sm">Create your first API key to access the analytics endpoint</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Admin;